import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, ImageUp, Lock, Plus, Send, Trash2, Type } from 'lucide-react';
import { PageLoader } from '../../components/Spinner';
import { StudioCanvas } from '../../components/studio/StudioCanvas';
import { FontPicker } from '../../components/studio/FontPicker';
import { ColorField, PanelHeading, Segmented, SliderRow, Stepper, SwitchRow } from '../../components/studio/controls';
import { FOCUS_RING, TEXT_INPUT } from '../../components/studio/constants';
import { useDesignHistory } from '../../components/studio/useDesignHistory';
import { addText, duplicateItem, findItem, moveLayer, removeItem, updateItem } from '../../components/studio/designUtils';
import { assetUrl } from '../../components/design/DesignLayers';
import { API_URL } from '../../utils/api';
import { EDITOR_FONTS, loadEditorFonts, loadFontStylesheets } from '../../utils/fonts';
import { getErrorMessage } from '../../utils/errors';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { cn } from '../../lib/utils';
import { STATUS_LABEL, StatusPill } from './partnerStatus';

// Text field keys the partner API keeps (app/routers/partners.py). Phone settings never reach this page.
const SAVE_KEYS = [
  'id', 'label', 'x', 'y', 'font_size', 'font_family', 'color', 'text_align', 'max_length', 'line_width',
  'line_spacing', 'static', 'options', 'prefix', 'suffix', 'hidden', 'field_input_id',
];

const toCanvas = (fields = []) =>
  fields.map((f) => ({ ...f, text: f.default_text ?? '', custom: !f.field_input_id }));

const toSave = (fields = []) =>
  fields.map((f) => {
    const out = { default_text: f.text ?? '' };
    SAVE_KEYS.forEach((k) => {
      if (f[k] !== undefined) out[k] = f[k];
    });
    return out;
  });

const snapshotOf = (name, design, background) => JSON.stringify({ name, f: toSave(design.text_fields), background });

const sideBtn = cn(
  'h-10 w-full rounded-[10px] border border-[#DCD6C8] bg-white px-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-cream disabled:opacity-50',
  FOCUS_RING
);

export default function PartnerDesigner() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [name, setName] = useState('');
  const [background, setBackground] = useState('');
  const [category, setCategory] = useState(null);
  const [fontList, setFontList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selection, setSelection] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState('');
  const fileRef = useRef(null);
  const { design, change, reset, undo, redo, breakMerge } = useDesignHistory({ text_fields: [], elements: [], background: null });

  useDocumentTitle(name ? `Design ${name}` : 'Design template');

  // ---- load ----
  useEffect(() => {
    loadEditorFonts();
    let cancelled = false;
    Promise.all([
      axios.get(`${API_URL}/partner/templates/${templateId}`),
      axios.get(`${API_URL}/partner/categories`),
      axios.get(`${API_URL}/partner/fonts`).catch(() => ({ data: [] })),
    ])
      .then(([t, cats, ownFonts]) => {
        if (cancelled) return;
        setTpl(t.data);
        setName(t.data.name || '');
        setBackground(t.data.background_image || '');
        setCategory((cats.data || []).find((c) => c.id === t.data.category_id) || null);
        const own = Array.isArray(ownFonts.data) ? ownFonts.data : [];
        // A partner's templates use the built-in fonts and the partner's own fonts only (fontScope.js):
        // Azamein's uploaded fonts stay on Azamein backgrounds.
        loadFontStylesheets(own);
        const builtIn = EDITOR_FONTS.map((f) => ({ family: f.family, label: f.family, hebrew: f.hebrew, decorative: f.decorative }));
        const mine = own.map((f) => ({ family: f.font_family, label: f.name, uploaded: true }));
        setFontList([...builtIn, ...mine]);
        const d = { text_fields: toCanvas(t.data.text_fields), elements: [], background: null };
        reset(d);
        setSaved(snapshotOf(t.data.name || '', d, t.data.background_image || ''));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(getErrorMessage(err, 'Could not open this template'));
        navigate('/partner');
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, navigate, reset]);

  const dirty = tpl && snapshotOf(name, design, background) !== saved;
  const locked = tpl?.status === 'pending';

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // ---- editing ----
  const selected = findItem(design, selectedId);
  const field = selected?.kind === 'text' ? selected.item : null;
  const patch = useCallback(
    (id, p, key) => change((d) => updateItem(d, id, p), { key }),
    [change]
  );

  const handleAddText = () => {
    let createdId = null;
    change((d) => {
      const r = addText(d, { label: 'Wording', text: 'מזל טוב', font_size: 30, static: true, field_input_id: null });
      createdId = r.id;
      return r.design;
    });
    if (createdId) setSelectedId(createdId);
  };

  const handleRemove = (id = selectedId) => {
    const found = findItem(design, id);
    if (!found || !found.item.custom) {
      toast.message('Category fields can’t be removed. Move them where you want them on the design.');
      return;
    }
    change((d) => removeItem(d, id));
    setSelectedId(null);
  };

  const actions = {
    duplicate: () => {
      let createdId = null;
      change((d) => {
        const r = duplicateItem(d, selectedId);
        createdId = r.id;
        return updateItem(r.design, r.id, { field_input_id: null, static: true });
      });
      if (createdId) setSelectedId(createdId);
    },
    move: (dir) => change((d) => moveLayer(d, selectedId, dir)),
    toggleLock: () => {},
    remove: () => handleRemove(),
    duplicateMany: () => {},
    removeMany: () => selection.forEach((id) => handleRemove(id)),
    lockMany: () => {},
    shortcuts: () => toast.message('Drag to move · Delete removes your own text · Ctrl+Z to undo'),
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        handleRemove();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---- background ----
  const uploadBackground = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API_URL}/partner/upload/background`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBackground(data.url);
      toast.success('Background uploaded');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ---- save / publish ----
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/partner/templates/${templateId}`, {
        name,
        background_image: background,
        text_fields: toSave(design.text_fields),
      });
      setTpl(data);
      const d = { text_fields: toCanvas(data.text_fields), elements: [], background: null };
      // the server puts back required fields and drops unknown keys: show exactly what was stored
      change(() => d);
      setSaved(snapshotOf(data.name || '', d, data.background_image || ''));
      setName(data.name || '');
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (dirty && !(await save())) return;
    try {
      const { data } = await axios.post(`${API_URL}/partner/templates/${templateId}/submit`);
      setTpl(data);
      toast.success('Sent to Azamein for review. You’ll see it go live here once it’s approved.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not send for review'));
    }
  };

  const withdraw = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/partner/templates/${templateId}/withdraw`);
      setTpl(data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not withdraw'));
    }
  };

  const fieldsByY = useMemo(
    () => design.text_fields.slice().sort((a, b) => (a.y ?? 50) - (b.y ?? 50)),
    [design.text_fields]
  );

  if (!tpl) return <PageLoader label="Opening the designer" />;

  const set = (prop, value) => field && patch(field.id, { [prop]: value }, `${field.id}-${prop}`);

  return (
    <div className="h-screen flex flex-col bg-[#EDEAE3] overflow-hidden">
      {/* top bar */}
      <header className="shrink-0 h-14 bg-white border-b border-[#E6E1D6] flex items-center gap-3 px-3 sm:px-4">
        <Link to="/partner" className={cn('h-9 w-9 rounded-full flex items-center justify-center hover:bg-cream', FOCUS_RING)} aria-label="Back to my templates">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          aria-label="Template name"
          disabled={locked}
          className={cn(TEXT_INPUT, 'h-9 max-w-[260px] flex-1 min-w-0')}
          data-testid="partner-template-name"
        />
        <StatusPill status={tpl.status} live={tpl.live_active} />
        <div className="ml-auto flex items-center gap-2">
          {locked ? (
            <button type="button" onClick={withdraw} className={cn(sideBtn, 'w-auto')}>
              Withdraw to edit
            </button>
          ) : (
            <>
              <button type="button" onClick={save} disabled={saving || !dirty} className={cn(sideBtn, 'w-auto')} data-testid="partner-save">
                {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving || !background || (tpl.status === 'approved' && !tpl.has_unpublished_changes && !dirty)}
                className={cn('h-10 rounded-[10px] bg-ink text-paper px-4 text-sm font-bold flex items-center gap-2 hover:bg-ink/90 disabled:opacity-50', FOCUS_RING)}
                data-testid="partner-submit"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{tpl.live_template_id ? 'Publish changes' : 'Publish'}</span>
              </button>
            </>
          )}
        </div>
      </header>

      {tpl.status === 'rejected' && tpl.review_note ? (
        <div role="alert" className="shrink-0 bg-[#FFF4E5] border-b border-[#F3D19C] px-4 py-2 text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-[#B26A00]" aria-hidden="true" />
          <span>
            <strong>Changes requested:</strong> {tpl.review_note}
          </span>
        </div>
      ) : null}
      {locked ? (
        <div className="shrink-0 bg-cream border-b border-[#E6E1D6] px-4 py-2 text-sm flex items-center gap-2">
          <Lock className="h-4 w-4" aria-hidden="true" /> Waiting for Azamein to review. Withdraw it if you need to make changes.
        </div>
      ) : null}

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        {/* left: category, background, fields */}
        <aside aria-label="Template setup" className="lg:w-[280px] shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E6E1D6] px-4 py-4 overflow-y-auto max-h-[38vh] lg:max-h-none flex flex-col gap-4">
          <div>
            <PanelHeading>Category</PanelHeading>
            <p className="text-sm font-bold">{category?.name || '—'}</p>
            <p className="text-xs text-[#6B665C] mt-1">
              The category&rsquo;s fields are added for you so customers can fill them in by phone, text message or online.
            </p>
          </div>

          <div>
            <PanelHeading>Background</PanelHeading>
            {background ? (
              <img src={assetUrl(background)} alt="" className="w-full aspect-[8.5/11] object-cover rounded-lg border border-[#E6E1D6] mb-2" />
            ) : (
              <p className="text-xs text-[#6B665C] mb-2">Upload your design as a JPG or PNG, 8.5 × 11 in portrait. 2550 × 3300 px prints sharpest.</p>
            )}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadBackground(e.target.files?.[0])} />
            <button type="button" className={sideBtn} disabled={locked || uploading} onClick={() => fileRef.current?.click()} data-testid="partner-upload-bg">
              <ImageUp className="h-4 w-4" aria-hidden="true" />
              {uploading ? 'Uploading…' : background ? 'Replace background' : 'Upload background'}
            </button>
          </div>

          <div>
            <PanelHeading>Text fields</PanelHeading>
            <ul className="flex flex-col gap-1">
              {fieldsByY.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(f.id)}
                    className={cn(
                      'w-full text-left rounded-lg px-2.5 py-2 text-sm flex items-center gap-2 hover:bg-cream',
                      selectedId === f.id && 'bg-cream ring-1 ring-ink/20',
                      FOCUS_RING
                    )}
                  >
                    <Type className="h-3.5 w-3.5 shrink-0 text-[#6B665C]" aria-hidden="true" />
                    <span className="truncate flex-1">{f.label || 'Text'}</span>
                    {f.field_input_id ? (
                      <span className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A6D1F] bg-[#F6EDD3] rounded px-1.5 py-0.5">Required</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#6B665C]">{f.static ? 'Fixed' : 'Editable'}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className={cn(sideBtn, 'mt-2')} onClick={handleAddText} disabled={locked}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Add text
            </button>
          </div>
        </aside>

        {/* canvas */}
        <main id="main-content" className="flex-1 min-h-[320px] flex flex-col">
          {background ? (
            <StudioCanvas
              design={design}
              selectedId={selectedId}
              selection={selection}
              onSelectionChange={(ids) => {
                setSelection(ids);
                setSelectedId(ids[ids.length - 1] || null);
              }}
              onSelect={setSelectedId}
              onTextEdit={(id, text) => patch(id, { text }, `${id}-text`)}
              onChange={locked ? () => false : change}
              onGestureEnd={breakMerge}
              backgroundImage={assetUrl(background)}
              interactive={!locked}
              actions={locked ? null : actions}
              footerNote={`8.5 × 11 in · ${design.text_fields.length} text field${design.text_fields.length === 1 ? '' : 's'}`}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className={cn('rounded-2xl border-2 border-dashed border-[#C9C2B2] bg-white/60 px-10 py-14 text-center hover:bg-white', FOCUS_RING)}
              >
                <ImageUp className="h-8 w-8 mx-auto mb-3 text-[#6B665C]" aria-hidden="true" />
                <span className="block font-bold">Upload your background to start</span>
                <span className="block text-sm text-[#6B665C] mt-1">JPG or PNG, 8.5 × 11 in portrait</span>
              </button>
            </div>
          )}
        </main>

        {/* right: selected field */}
        <aside aria-label="Text settings" className="lg:w-[300px] shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-[#E6E1D6] px-4 py-4 overflow-y-auto max-h-[45vh] lg:max-h-none">
          {field ? (
            <fieldset disabled={locked} className="flex flex-col gap-3.5" data-testid="partner-field-panel">
              <div>
                <PanelHeading>{field.field_input_id ? 'Category field' : 'Your text'}</PanelHeading>
                {field.field_input_id ? (
                  <p className="text-xs text-[#6B665C]">
                    Customers fill this in. The sample text below only shows on the design while you work.
                  </p>
                ) : null}
              </div>
              <label className="flex flex-col gap-1 text-xs font-bold">
                Field name
                <input value={field.label || ''} maxLength={80} onChange={(e) => set('label', e.target.value)} className={cn(TEXT_INPUT, 'h-9 font-normal')} />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold">
                {field.field_input_id ? 'Sample text' : 'Text'}
                <textarea
                  dir="auto"
                  value={field.text || ''}
                  maxLength={2000}
                  rows={Math.min(5, Math.max(2, ((field.text || '').match(/\n/g) || []).length + 1))}
                  onChange={(e) => set('text', e.target.value)}
                  className="rounded-xl px-3 py-2.5 font-hebrew text-[17px] font-normal leading-snug resize-y border border-[#DCD6C8] outline-none focus-visible:ring-[3px] focus-visible:ring-pink/10"
                />
              </label>
              {field.field_input_id ? (
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1 text-xs font-bold">
                    Words before
                    <input dir="auto" value={field.prefix || ''} maxLength={200} onChange={(e) => set('prefix', e.target.value || null)} className={cn(TEXT_INPUT, 'h-9 font-normal')} />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-bold">
                    Words after
                    <input dir="auto" value={field.suffix || ''} maxLength={200} onChange={(e) => set('suffix', e.target.value || null)} className={cn(TEXT_INPUT, 'h-9 font-normal')} />
                  </label>
                </div>
              ) : (
                <SwitchRow label="Customers can't change this wording" checked={Boolean(field.static)} onChange={(v) => set('static', v)} />
              )}

              <FontPicker value={field.font_family} fonts={fontList} onChange={(v) => set('font_family', v)} sampleText={field.text} />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold">Size</span>
                <Stepper value={field.font_size ?? 24} min={6} max={400} onChange={(v) => set('font_size', v)} label="Font size" />
              </div>
              <ColorField label="Color" value={field.color || '#1A1A1A'} onChange={(v) => set('color', v)} />
              <Segmented
                label="Alignment"
                options={[['right', 'Right'], ['center', 'Center'], ['left', 'Left']]}
                value={field.text_align || 'center'}
                onChange={(v) => set('text_align', v)}
              />
              <SliderRow label="Line width" value={field.line_width ?? 80} min={10} max={100} onChange={(v) => set('line_width', v)} format={(v) => `${v}%`} />
              <SliderRow label="Line spacing" value={field.line_spacing ?? 1.2} min={0.6} max={3} step={0.05} onChange={(v) => set('line_spacing', v)} format={(v) => `${Number(v).toFixed(2)}×`} />
              <label className="flex items-center justify-between gap-3 text-xs font-bold">
                Max characters
                <input
                  type="number"
                  min={1}
                  max={2000}
                  value={field.max_length ?? 200}
                  onChange={(e) => set('max_length', Math.max(1, Math.min(2000, Number(e.target.value) || 200)))}
                  className={cn(TEXT_INPUT, 'h-9 w-24 font-normal')}
                />
              </label>
              {field.custom ? (
                <button type="button" onClick={() => handleRemove(field.id)} className={cn(sideBtn, 'text-[#B0003A]')}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Remove this text
                </button>
              ) : null}
            </fieldset>
          ) : (
            <div className="text-sm text-[#6B665C] flex flex-col gap-2">
              <PanelHeading>How it works</PanelHeading>
              <p>Click a text field on the design to style it. Drag it to move it.</p>
              <p>Fields marked <strong>Required</strong> come from the category. Customers fill them in, so they can be styled and moved but not removed.</p>
              <p>Use <strong>Add text</strong> for your own wording.</p>
              <p>When it looks right, press <strong>Publish</strong>. Azamein reviews it, then it goes live. Status: {STATUS_LABEL[tpl.status] || tpl.status}.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
